import React, { useState, useEffect, Fragment } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  useHistory,
  Redirect,
  useLocation,
  useParams,
} from "react-router-dom";
import "./index.scss";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { useSelector } from "react-redux";
import Host from "../../Components/Host";
import HelmetComponent from "../../Components/HelmetComponent";
import { useGetUserDataQuery } from "../../services/login";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import { getMetaByCountry } from "../../metaConfig";

function ReturnsRefund() {
  const { userId, userToken, userEmail, userName, isMember, isPaymentMethod, languageCode, countryCode } = useSelector(
    (state) => state.auth
  );
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [getSearchId, setSearchId] = useState("");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      // behavior: "smooth"
    });
  }, []);

  const { data: userData } = useGetUserDataQuery(
    { userId: userId, userToken: userToken, countryCode: countryCode },
    { count: 1 },
    { refetchOnMountOrArgChange: true }
  );

  const [isMetaTitle, setMetaTitle] = useState("");
  useEffect(() => {
    const { title } = getMetaByCountry(countryCode);
    setMetaTitle(title);
  }, [countryCode]);

  return (
    <>
      <HelmetComponent title={"Returns, Warranty and Refunds"} metaTitle={isMetaTitle} />
      <div className="statisc-page">
        <div className="container static-container">
          <div className="row static-row">
            <div className="col-md-10">
              <div
                className="static-container-card"
                style={{
                  textAlign: "left",
                  padding: "20px",
                  background: "#fcfcfc",
                  border: "1px solid #eee",
                  borderRadius: "7px",
                }}
              >
                <h3 style={{ padding: "20px 0px 20px 0px", fontWeight: 300, fontSize: "1.4rem" }}>
                  {t("Returns, Warranty and Refunds")}
                </h3>

                <p>
                  At {Host?.domainName2}, we want customers to shop with confidence. This policy explains how returns,
                  refunds, cancellations, damaged items, defective items, incorrect items, and warranty claims are
                  handled for orders placed on {window.location.hostname}.
                </p>
                <p>
                  Please read this policy carefully before placing an order. By placing an order on our website, you
                  agree to the terms below.
                </p>

                <h4>1. Return Window</h4>
                <p>
                  Customers may request a return within 7 days from the date of delivery only for eligible reasons
                  listed in this policy.
                </p>
                <p>To be eligible for a return, the product must be:</p>
                <p style={{ marginBottom: "5px" }}>• unused, unless the issue is a verified defect;</p>
                <p style={{ marginBottom: "5px" }}>• in its original packaging;</p>
                <p style={{ marginBottom: "5px" }}>
                  • complete with all accessories, manuals, tags, labels, warranty cards, and invoices;
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • not damaged, altered, or missing any parts due to customer handling.
                </p>
                <p>Returned products will be inspected before approval.</p>

                <h4>2. Items Eligible for Return</h4>
                <p>A return may be accepted only if:</p>
                <p style={{ marginBottom: "5px" }}>• the wrong item was delivered;</p>
                <p style={{ marginBottom: "5px" }}>• the item arrived damaged;</p>
                <p style={{ marginBottom: "5px" }}>• the item is defective;</p>
                <p style={{ marginBottom: "5px" }}>• the item does not match the product description on our website;</p>
                <p style={{ marginBottom: "5px" }}>• the item is eligible for warranty support, where applicable.</p>
                <p>Returns are subject to inspection and approval.</p>

                <h4>3. Change of Mind Returns</h4>
                <p>
                  We do not accept returns, refunds, or exchanges for change of mind, buyer's remorse, incorrect
                  selection by the customer, or products ordered by mistake after the order has been shipped.
                </p>
                <p>
                  Customers are requested to review product details, specifications, size, compatibility, delivery
                  timelines, warranty information, and pricing carefully before placing an order.
                </p>
                <p>
                  Customers may request cancellation of an order before it has been shipped in accordance with Section
                  11 of this policy.
                </p>
                <p>
                  This does not affect the customer's right to request support if the item received is damaged,
                  defective, incorrect, or not as described.
                </p>

                <h4>4. Items Not Eligible for Return</h4>
                <p>
                  For hygiene, safety, legal, or product-condition reasons, some items may not be eligible for return,
                  including:
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • products returned due to change of mind, buyer's remorse, incorrect selection by the customer, or
                  products ordered by mistake after shipment;
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • used, damaged, or opened products that cannot be resold, except where the issue is a verified
                  defect;
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • products missing original packaging, accessories, manuals, tags, labels, warranty cards, or
                  invoices;
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • personal care, hygiene, beauty, grooming, or health-related items once opened;
                </p>
                <p style={{ marginBottom: "5px" }}>• consumable products once opened;</p>
                <p style={{ marginBottom: "5px" }}>• software, digital products, gift cards, or downloadable items;</p>
                <p style={{ marginBottom: "5px" }}>
                  • products damaged due to misuse, improper handling, installation error, or unauthorized repair;
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • items marked as Final Sale, Non-Returnable, or Clearance, where clearly mentioned on the product
                  page.
                </p>
                <p>
                  This does not affect the customer's right to request support for damaged, defective, incorrect, or
                  not-as-described products.
                </p>

                <h4>5. Damaged, Defective, Incorrect, or Not-as-Described Items</h4>
                <p>
                  If your item is delivered damaged, defective, incorrect, or not as described, please contact us within
                  3 days of delivery.
                </p>
                <p>Please provide:</p>
                <p style={{ marginBottom: "5px" }}>• order number;</p>
                <p style={{ marginBottom: "5px" }}>• clear photos or video of the product;</p>
                <p style={{ marginBottom: "5px" }}>• photos of the packaging;</p>
                <p style={{ marginBottom: "5px" }}>• a description of the issue.</p>
                <p>
                  After reviewing the request, we may arrange a return pickup, replacement, repair, or refund, depending
                  on the product condition, inspection result, warranty coverage, and availability.
                </p>
                <p>
                  Claims reported after 3 days may still be reviewed, but approval will depend on the product type,
                  issue, available evidence, and inspection result.
                </p>

                <h4>6. Return Process and Pickup</h4>
                <p>To request a return, contact our support team:</p>
                <p>Email: {Host?.email}</p>
                <p>Support Hours: {Host?.customerSupport}</p>
                <p>
                  Please include your order number, the reason for the return, and any supporting photos or videos where
                  applicable.
                </p>
                <p>
                  Once your return request has been reviewed and approved, {Host?.domainName2} will arrange for the item
                  to be collected from you by an authorized courier or delivery partner.
                </p>
                <p>
                  For approved returns, {Host?.domainName2} will provide the required return shipping label and return
                  instructions. Customers are not required to create or purchase their own return shipping label for an
                  approved eligible return.
                </p>
                <p>
                  Please securely repack the item in its original packaging, together with all accessories, manuals,
                  tags, labels, warranty cards, invoices, and other items originally supplied with the product.
                </p>
                <p>
                  Do not send or return any item without prior return approval from {Host?.domainName2}. Unapproved
                  returns may not be accepted or processed.
                </p>

                <h4>7. Returns Address</h4>
                <p>Approved returns will normally be collected by a courier arranged by {Host?.domainName2}.</p>
                <p>
                  Where our support team specifically instructs a customer to send an approved return to us, the return
                  address is:
                </p>
                <div style={{ fontSize: "14px" }}>
                  <strong>{Host?.companyName}</strong>
                </div>
                <p style={{ lineHeight: 2 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: Host?.address,
                    }}
                  ></div>
                  <MailOutlineIcon style={{ fontSize: "18px" }} /> {Host?.email} <br />
                  <PhoneIcon style={{ fontSize: "18px" }} /> {Host?.phone}
                </p>
                <p>
                  Customers should not independently send products to this address unless instructed to do so by our
                  support team.
                </p>

                <h4>8. Return Shipping Fees</h4>
                <p>
                  For returns approved under this policy because the item was damaged, defective, incorrect, or not as
                  described, {Host?.domainName2} will arrange and pay for the return shipping.
                </p>
                <p>Return shipping responsibility is as follows:</p>
                <p style={{ marginBottom: "5px" }}>
                  • Wrong item delivered — Return shipping paid by {Host?.domainName2} after verification
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • Damaged item delivered — Return shipping paid by {Host?.domainName2} after verification
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • Defective item — Return shipping paid by {Host?.domainName2} after verification
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • Item not as described — Return shipping paid by {Host?.domainName2} after verification
                </p>
                <p style={{ marginBottom: "5px" }}>
                  • Change of mind / ordered by mistake after shipment — Not eligible for return
                </p>
                <p style={{ marginBottom: "5px" }}>• Product not in original condition — Return may be rejected</p>
                <p>
                  For an approved eligible return, {Host?.domainName2} will arrange the collection and provide the
                  return shipping label. Customers will not be required to pay the courier directly for an approved
                  return covered by {Host?.domainName2}.
                </p>
                <p>
                  If a returned item is found to be used, damaged by the customer, missing parts, or otherwise not
                  eligible under this policy, the return may be rejected and the item may be sent back to the customer.
                </p>

                <h4>9. Refunds and Processing Time</h4>
                <p>
                  Once the returned item has been received, it will be inspected to confirm that it meets the
                  eligibility requirements of this policy.
                </p>
                <p>
                  We will notify the customer whether the refund has been approved or rejected following inspection.
                </p>
                <p>
                  If approved, the refund will be processed within 7 to 15 business days after the returned item has
                  been received, inspected, and approved.
                </p>
                <p>The refund will normally be issued to the original payment method where possible.</p>
                <p>
                  After {Host?.domainName2} processes the refund, additional time may be required for the amount to
                  appear in the customer's account. This depends on the customer's bank, card issuer, or payment service
                  provider and is outside {Host?.domainName2}'s direct control.
                </p>
                <p>
                  Original shipping charges, cash-on-delivery charges, service fees, or payment processing charges may
                  be non-refundable unless the return is due to our error, a damaged item, defective item, incorrect
                  item, or item not as described.
                </p>

                <h4>10. Replacements</h4>
                <p>Where available, customers may request a replacement instead of a refund.</p>
                <p>
                  A replacement may be offered if the same item is available, the issue is verified, and the original
                  item is returned in accordance with this policy.
                </p>
                <p>If a replacement is not available, we may offer a refund or an alternative product option.</p>

                <h4>11. Order Cancellations</h4>
                <p>Customers may request cancellation of an order at any time before the order has been shipped.</p>
                <p>To request a cancellation, contact our support team at:</p>
                <p>Email: {Host?.email}</p>
                <p>Please provide your order number and clearly state that you would like to cancel the order.</p>
                <p>Our team will check the shipment status and confirm whether the cancellation can be completed.</p>
                <p>
                  If the order has not yet been shipped, the cancellation will be processed and any eligible amount
                  already paid will be refunded to the original payment method where possible.
                </p>
                <p>
                  Once an order has been shipped, it can no longer be cancelled. Any request made after shipment will be
                  handled in accordance with the return eligibility requirements set out in this policy.
                </p>
                <p>
                  If an item becomes unavailable after an order is placed, we will notify the customer and offer one of
                  the following options:
                </p>
                <p style={{ marginBottom: "5px" }}>• wait for availability;</p>
                <p style={{ marginBottom: "5px" }}>• choose an alternative product;</p>
                <p style={{ marginBottom: "5px" }}>
                  • cancel the item and receive a full refund for the unavailable item.
                </p>

                <h4>12. Product Availability and Pricing</h4>
                <p>
                  We make reasonable efforts to keep product availability, prices, and delivery estimates accurate and
                  updated.
                </p>
                <p>
                  In rare cases, an item may become unavailable due to supplier-side stock changes, system updates, or
                  fulfillment limitations. If this happens after an order is placed, we will contact the customer
                  promptly and provide available options, including a full refund for the affected item.
                </p>
                <p>Customers will not be charged any additional amount without confirmation.</p>

                <h4>13. Warranty</h4>
                <p>
                  Some products may include a manufacturer warranty, seller warranty, or supplier warranty. Warranty
                  coverage depends on the product type and the warranty information shown on the product page or
                  included with the item.
                </p>
                <p>Warranty does not usually cover:</p>
                <p style={{ marginBottom: "5px" }}>• physical damage;</p>
                <p style={{ marginBottom: "5px" }}>• water damage;</p>
                <p style={{ marginBottom: "5px" }}>• misuse or negligence;</p>
                <p style={{ marginBottom: "5px" }}>• unauthorized repair;</p>
                <p style={{ marginBottom: "5px" }}>• normal wear and tear;</p>
                <p style={{ marginBottom: "5px" }}>• missing accessories or packaging;</p>
                <p style={{ marginBottom: "5px" }}>• damage caused by incorrect installation or handling.</p>
                <p>
                  For warranty support, customers should contact us with the order number, product details, and issue
                  description.
                </p>

                <h4>14. Refunds for Cash on Delivery Orders</h4>
                <p>
                  For Cash on Delivery orders, approved refunds may be issued through bank transfer, store credit,
                  wallet credit, or another available refund method confirmed by our support team.
                </p>
                <p>Customers may be required to provide valid bank or payment details to complete the refund.</p>

                <h4>15. Inspection and Approval</h4>
                <p>All returns are subject to inspection.</p>
                <p>{Host?.domainName2} reserves the right to reject a return if:</p>
                <p style={{ marginBottom: "5px" }}>• the item is not in original condition;</p>
                <p style={{ marginBottom: "5px" }}>• the item has been used, damaged, or altered by the customer;</p>
                <p style={{ marginBottom: "5px" }}>
                  • accessories, packaging, manuals, warranty cards, or invoices are missing;
                </p>
                <p style={{ marginBottom: "5px" }}>• the return request is outside the allowed return period;</p>
                <p style={{ marginBottom: "5px" }}>• the product is listed as non-returnable;</p>
                <p style={{ marginBottom: "5px" }}>• the issue is not verified after inspection.</p>

                <h4>16. Contact Information</h4>
                <p>For returns, refunds, cancellations, or warranty support, please contact:</p>
                <div style={{ fontSize: "14px" }}>
                  <strong>Trade Name: {Host?.domainName2}</strong>
                  <br />
                  <strong>Trade License: {Host?.companyName}</strong>
                  <br />
                  <strong>License Number: {Host?.licenseNumber}</strong>
                </div>
                <p style={{ lineHeight: 2 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: Host?.address,
                    }}
                  ></div>
                  <MailOutlineIcon style={{ fontSize: "18px" }} /> {Host?.email} <br />
                  <PhoneIcon style={{ fontSize: "18px" }} /> {Host?.phone}
                </p>
                <p>Support Hours: {Host?.customerSupport}</p>
                <p>Website: {window.location.hostname}</p>

                <p>&nbsp;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ReturnsRefund;
